import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Typography,
    Box
} from '@mui/material';
import { UserDataFieldDto, BusinessDataFieldType } from '../../types/customUserDataFields';

interface CustomFieldDialogProps {
    field: UserDataFieldDto | null;
    open: boolean;
    onClose: () => void;
    onSave: (fieldId: string, value: string) => Promise<void>;
}

const CustomFieldDialog: React.FC<CustomFieldDialogProps> = ({
    field,
    open,
    onClose,
    onSave
}) => {
    const [value, setValue] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (field) {
            setValue(field.value || '');
            setError('');
        }
    }, [field]);

    const handleSave = async () => {
        if (!field) return;

        try {
            setIsSaving(true);
            setError('');
            await onSave(field.businessDataFieldId, value);
        } catch (err) {
            console.error('Error saving field:', err);
            setError('Failed to save changes');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (!isSaving) {
            setValue('');
            setError('');
            onClose();
        }
    };

    const renderFieldInput = () => {
        if (!field) return null;

        const { type, name, description } = field.businessDataField;

        switch (type) {
            case BusinessDataFieldType.Bool:
                return (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={value === 'true'}
                                onChange={(e) => setValue(e.target.checked ? 'true' : 'false')}
                                color="primary"
                            />
                        }
                        label={`${name} ${value === 'true' ? '(Yes)' : '(No)'}`}
                    />
                );

            case BusinessDataFieldType.Text:
                return (
                    <TextField
                        fullWidth
                        label={name}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        helperText={description}
                        inputProps={{ maxLength: 2000 }}
                        multiline={false}
                    />
                );

            case BusinessDataFieldType.MultilineText:
                return (
                    <TextField
                        fullWidth
                        label={name}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        helperText={description}
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 2000 }}
                    />
                );

            default:
                return (
                    <Typography color="error">
                        Unsupported field type: {type}
                    </Typography>
                );
        }
    };

    if (!field) return null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit {field.businessDataField.name}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    {field.businessDataField.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {field.businessDataField.description}
                        </Typography>
                    )}

                    {renderFieldInput()}

                    {error && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isSaving}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={isSaving}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomFieldDialog;
